import React, { useState } from 'react';
import { Shield, ShieldCheck, Fingerprint, Smartphone, QrCode, AlertTriangle, ArrowRight, Lock, Key, Eye, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Staff2FASetupRequired — full-screen "please set up 2FA" reminder page.
 *
 * Shown every session after login until the user has actually enrolled a TOTP secret.
 * "Set up now" walks them through QR + verify inline; "Continue for now" flips a session
 * flag so the dashboard opens (they'll be reminded again on next login).
 */
const Staff2FASetupRequired = ({ currentUser, getAuthHeaders, onEnabled, onSkipForSession }) => {
  const [stage, setStage] = useState('intro'); // intro | qr | verifying | done
  const [qrCodeData, setQrCodeData] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/2fa/enable`, {}, { headers: getAuthHeaders() });
      setQrCodeData(r.data.qr_code);
      setTotpSecret(r.data.secret);
      setStage('qr');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start 2FA setup');
    }
    setBusy(false);
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/2fa/verify-setup`, { code: verifyCode }, { headers: getAuthHeaders() });
      toast.success('Two-factor authentication enabled');
      setStage('done');
      setTimeout(() => onEnabled && onEnabled(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] min-h-screen bg-[#05050A] flex items-center justify-center px-4 py-10 overflow-y-auto" data-testid="setup-2fa-required-page">
      <div className="fixed top-0 left-1/3 w-[700px] h-[500px] bg-[#8A2BE2]/[0.05] rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed bottom-0 right-1/3 w-[600px] h-[400px] bg-[#00E5FF]/[0.04] rounded-full blur-[160px] pointer-events-none" />

      <div className="relative w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-4 relative"
            style={{
              background: 'linear-gradient(135deg, rgba(138,43,226,0.20), rgba(244,63,94,0.15))',
              border: '1px solid rgba(138,43,226,0.35)',
              boxShadow: '0 20px 60px -12px rgba(138,43,226,0.55)',
            }}>
            <Fingerprint className="w-10 h-10 text-violet-300" strokeWidth={1.4} />
            <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-rose-500 border-4 border-[#05050A] flex items-center justify-center">
              <AlertTriangle className="w-3 h-3 text-white" strokeWidth={2.4} />
            </span>
          </div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-white/40 font-medium mb-1">Security Gate · Recommended</p>
          <h1 className="text-white text-3xl md:text-4xl font-light tracking-tight">Protect your Starnight account</h1>
          <p className="text-white/50 text-sm max-w-lg mx-auto mt-3 leading-relaxed">
            Two-factor authentication adds a second lock to your account so nobody can sign in with just a stolen password. It takes about 60 seconds.
          </p>
        </div>

        {/* Main card */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] backdrop-blur-md"
          style={{
            background: 'linear-gradient(140deg, rgba(10,10,20,0.75) 0%, rgba(8,8,18,0.9) 100%)',
            boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}>
          <div className="pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full blur-[120px] opacity-40 bg-violet-500/40" />

          {stage === 'intro' && (
            <div className="relative p-8 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                {[
                  { icon: Smartphone, hex: '#00E5FF', title: 'Grab an app', desc: 'Google Authenticator, Authy or 1Password' },
                  { icon: QrCode,     hex: '#8A2BE2', title: 'Scan the code', desc: "We'll show you a QR to scan" },
                  { icon: CheckCircle, hex: '#10B981', title: 'Enter 6 digits', desc: 'Confirm the code and you\'re done' },
                ].map((step, i) => (
                  <div key={i} className="relative overflow-hidden p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                    <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full blur-[60px] opacity-40" style={{ background: step.hex }} />
                    <div className="relative">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2.5" style={{ background: `${step.hex}18`, border: `1px solid ${step.hex}35` }}>
                        <step.icon className="w-4 h-4" style={{ color: step.hex }} strokeWidth={1.8} />
                      </div>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 font-medium mb-1">Step {i + 1}</p>
                      <p className="text-white text-sm font-medium">{step.title}</p>
                      <p className="text-white/50 text-[11px] mt-1 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25 mb-6">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200/90 text-xs leading-relaxed">
                  Skipping this today is fine — you'll see this reminder again at every sign-in until 2FA is on. It never blocks you from logging in.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={onSkipForSession}
                  data-testid="skip-2fa-btn"
                  className="text-white/50 hover:text-white/85 text-xs tracking-[0.15em] uppercase font-medium transition-colors"
                >
                  Continue for now →
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={startSetup}
                  data-testid="start-2fa-setup-btn"
                  className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all disabled:opacity-40 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8A2BE2, #F472B6)',
                    color: '#0a0a14',
                    boxShadow: '0 20px 45px -12px rgba(138,43,226,0.66)',
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Shield className="w-3.5 h-3.5 relative" />
                  <span className="relative">{busy ? 'Preparing…' : 'Set up 2FA now'}</span>
                </button>
              </div>
            </div>
          )}

          {stage === 'qr' && (
            <div className="relative p-8 md:p-10">
              <div className="text-center mb-6">
                <p className="text-[10px] tracking-[0.25em] uppercase text-violet-300 font-medium mb-1">Step 2 of 3</p>
                <h2 className="text-white text-xl font-light">Scan with your authenticator</h2>
              </div>

              <div className="flex justify-center mb-5">
                <div className="p-3 rounded-2xl bg-white shadow-[0_20px_50px_-15px_rgba(139,92,246,0.6)]">
                  <img src={qrCodeData} alt="2FA QR" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.06] mb-4">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5">Manual entry secret</p>
                <p className="text-white font-mono text-sm break-all select-all tracking-wider" data-testid="2fa-secret">{totpSecret}</p>
              </div>

              <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium block mb-1.5">Enter 6-digit code</label>
              <input
                type="text" inputMode="numeric" maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3.5 bg-black/40 border border-white/[0.08] rounded-xl text-white text-center text-2xl font-mono tracking-[0.5em] focus:border-violet-400/40 focus:outline-none"
                placeholder="000000" autoFocus data-testid="2fa-verify-code-input"
              />

              <div className="flex items-center justify-between gap-3 mt-5">
                <button type="button" onClick={() => setStage('intro')} className="text-white/50 hover:text-white/85 text-xs tracking-[0.15em] uppercase font-medium">← Back</button>
                <button
                  type="button" disabled={busy || verifyCode.length !== 6} onClick={verifySetup}
                  data-testid="2fa-verify-enable-btn"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase transition-all disabled:opacity-40 hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #10B981, #00E5FF)', color: '#0a0a14', boxShadow: '0 16px 35px -10px rgba(16,185,129,0.55)' }}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  {busy ? 'Verifying…' : 'Verify & enable'}
                </button>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div className="relative p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-emerald-500/15 border border-emerald-500/40 mb-4">
                <ShieldCheck className="w-10 h-10 text-emerald-400" strokeWidth={1.6} />
              </div>
              <h2 className="text-white text-2xl font-light tracking-tight">You're all set</h2>
              <p className="text-white/50 text-sm mt-2">Two-factor authentication is now active. Redirecting…</p>
            </div>
          )}
        </div>

        <p className="text-center text-white/30 text-[10px] tracking-[0.2em] uppercase mt-6">
          Signed in as {currentUser?.display_name || currentUser?.username}
        </p>
      </div>
    </div>
  );
};

export default Staff2FASetupRequired;
